import { useCallback, useEffect, useRef, useState } from "react";
import type { GridFilterModel } from "@mui/x-data-grid";
import { muiFilterModelHasActiveItems } from "../../../../utils/muiGridFilterModel";
import { getDefaultOperator } from "./filterOperators";

const FILTER_DEBOUNCE_MS = 500;

const EMPTY_FILTER_MODEL: GridFilterModel = { items: [] };

type UseHeaderFilterModelOptions = {
  filterModel?: GridFilterModel;
  onFilterModelChange?: (filterModel: GridFilterModel) => void;
};

export function useHeaderFilterModel({
  filterModel,
  onFilterModelChange,
}: UseHeaderFilterModelOptions) {
  const [localModel, setLocalModel] = useState<GridFilterModel>(
    filterModel ?? EMPTY_FILTER_MODEL,
  );
  // Operator chosen per field, kept even before a value is typed. Committed filter
  // items only ever hold fields that actually constrain rows, so the selected
  // operator needs its own home or it would be lost on every empty-value change.
  const [pendingOperators, setPendingOperators] = useState<
    Record<string, string>
  >({});

  // Mirror of localModel read synchronously inside event handlers, so updates never
  // run through a setState updater (which executes during render and would commit to
  // the parent mid-render — React's "cannot update a component while rendering").
  const localModelRef = useRef(localModel);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingModelRef = useRef<GridFilterModel | null>(null);
  const onChangeRef = useRef(onFilterModelChange);
  onChangeRef.current = onFilterModelChange;

  const setModel = useCallback((next: GridFilterModel) => {
    localModelRef.current = next;
    setLocalModel(next);
  }, []);

  useEffect(() => {
    if (filterModel !== undefined) {
      // An external reset/update must win over any in-flight debounced commit,
      // otherwise a stale pending model fires ~500ms later and clobbers the reset.
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      pendingModelRef.current = null;
      localModelRef.current = filterModel;
      setLocalModel(filterModel);
    }
  }, [filterModel]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const commitToParent = useCallback((nextModel: GridFilterModel) => {
    onChangeRef.current?.(nextModel);
  }, []);

  const scheduleCommit = useCallback(
    (nextModel: GridFilterModel) => {
      if (!onChangeRef.current) return;

      pendingModelRef.current = nextModel;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      if (!muiFilterModelHasActiveItems(nextModel)) {
        pendingModelRef.current = null;
        commitToParent(nextModel);
        return;
      }

      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        pendingModelRef.current = null;
        commitToParent(nextModel);
      }, FILTER_DEBOUNCE_MS);
    },
    [commitToParent],
  );

  const flushPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (pendingModelRef.current !== null && onChangeRef.current) {
      const model = pendingModelRef.current;
      pendingModelRef.current = null;
      onChangeRef.current(model);
    }
  }, []);

  const getItem = useCallback(
    (field: string) => localModel.items.find((i) => i.field === field),
    [localModel.items],
  );

  const getItemValue = useCallback(
    (field: string) => getItem(field)?.value,
    [getItem],
  );

  const getItemOperator = useCallback(
    (field: string, columnType?: string) =>
      getItem(field)?.operator ??
      pendingOperators[field] ??
      getDefaultOperator(columnType),
    [getItem, pendingOperators],
  );

  const updateItem = useCallback(
    (
      field: string,
      operator: string | undefined,
      value: unknown,
      columnType?: string,
    ) => {
      const op = operator ?? getDefaultOperator(columnType);

      // Remember the chosen operator even before a value exists, so the operator
      // control reflects the selection without injecting an empty (no-op) filter.
      setPendingOperators((prev) =>
        prev[field] === op ? prev : { ...prev, [field]: op },
      );

      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
      const valueLess = op === "isEmpty" || op === "isNotEmpty";

      const prev = localModelRef.current;
      const existing = prev.items.find((item) => item.field === field);

      if (isEmpty && !valueLess) {
        // No active value. With nothing committed for this field, this is a pure
        // operator switch — leave the model (and the parent fetch) untouched.
        if (!existing) {
          return;
        }
        // Otherwise the user cleared an existing value: drop it and refetch.
        const next = {
          ...prev,
          items: prev.items.filter((item) => item.field !== field),
        };
        setModel(next);
        scheduleCommit(next);
        return;
      }

      const next: GridFilterModel = {
        ...prev,
        items: [
          ...prev.items.filter((item) => item.field !== field),
          { field, operator: op, value, id: field },
        ],
        logicOperator: (prev.logicOperator ??
          "and") as GridFilterModel["logicOperator"],
      };
      setModel(next);
      scheduleCommit(next);
    },
    [scheduleCommit, setModel],
  );

  const clearItem = useCallback(
    (field: string) => {
      setPendingOperators((prev) => {
        if (!(field in prev)) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      });
      const prev = localModelRef.current;
      const next = {
        ...prev,
        items: prev.items.filter((item) => item.field !== field),
      };
      setModel(next);
      scheduleCommit(next);
    },
    [scheduleCommit, setModel],
  );

  const clearAll = useCallback(() => {
    setPendingOperators({});
    const prev = localModelRef.current;
    const next = { ...prev, items: [], quickFilterValues: undefined };
    setModel(next);
    scheduleCommit(next);
  }, [scheduleCommit, setModel]);

  return {
    localModel,
    getItemValue,
    getItemOperator,
    updateItem,
    clearItem,
    clearAll,
    flushPending,
  };
}

export type HeaderFilterModelApi = ReturnType<typeof useHeaderFilterModel>;
